import React, { useMemo, useState } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { Avatar, Button, Grid, styled } from "@mui/material";

import DocumentPreviewModal from "Components/Common/DocumentPreviewModal";
import { getOptionLabel, getOptionValue } from "common/enums";

import { getImageUrl } from "utils";
import { convertUTCDateToLocalDate } from "utils/convertUTCDateToLocalDate";

const isApplicationStatusNotAllowed = [
  "Saved",
  "Submitted",
  "Reviewed",
  "Commented",
  "Approved",
  "Rejected",
  "Processed",
];

const ProfileImage = styled(Avatar)`
  width: 143px;
  height: 143px;
  margin: 0 auto;
  @media (max-width: 768px) {
    width: 100px;
    height: 100px;
    margin: 0;
  }
`;

const ApplicationInfoView = ({
  applicationInfo: {
    imageUrl,
    applicationNumber,
    applicationTypeName,
    submissionDate,
    demandEstimateUrl,
    demandEstimateReceiptUrl,
    consumerNumber,
    consumerName,
    disconnectionDate,
    consumerDisconnectionDate,
    salesOrder,
    serialNumber,
    utilities = [],
    applicationStatusName,
  },
  isOtherUtility = false,
  t,
}) => {
  const [documentToPreview, setDocumentToPreview] = useState();

  const profileURL = useMemo(
    () => (imageUrl ? getImageUrl(imageUrl) : "/broken-image.jpg"),
    [imageUrl]
  );

  const isSolidWasteApplication = useMemo(
    () => utilities[0] === getOptionValue("utilityOptions", "Solid Waste"),
    [utilities]
  );

  const isViewDemandEstimateBtnVisible = useMemo(
    () =>
      !!demandEstimateUrl &&
      !isApplicationStatusNotAllowed.includes(applicationStatusName),
    [demandEstimateUrl, applicationStatusName]
  );

  return (
    <>
      {!!documentToPreview && (
        <DocumentPreviewModal
          isOpen
          handleClose={() => setDocumentToPreview(null)}
          {...documentToPreview}
        />
      )}

      <Grid container columnSpacing={2}>
        <Grid item xs={12} lg={4} md={4}>
          <ProfileImage src={profileURL} />
        </Grid>
        <Grid
          container
          item
          className="justify-content-center"
          xs={12}
          lg={8}
          md={8}
        >
          <Grid item lg={6} md={6}>
            <p className="m-0">{t("Application Number")}</p>
            <p className="fw-semibold mb-1">{applicationNumber ?? "-"}</p>
            <p className="m-0">{t("Application Type")}</p>
            <p className="fw-semibold mb-1">{applicationTypeName ?? "-"}</p>
            <p className="m-0">{t("Utilities")}</p>
            <p className="fw-semibold mb-1">
              {utilities.length
                ? utilities
                    .map((value) => getOptionLabel("utilityOptions", value))
                    .join(", ")
                : "-"}
            </p>
            <p className="m-0">{t("Application Date")}</p>
            <p className="fw-semibold mb-1">
              {submissionDate
                ? convertUTCDateToLocalDate(submissionDate).format("DD-MM-YYYY")
                : "-"}
            </p>

            {!!disconnectionDate && (
              <>
                <p className="m-0">{t("Expected Disconnection Date")}</p>
                <p className="fw-semibold mb-1">
                  {convertUTCDateToLocalDate(disconnectionDate).format(
                    "DD-MM-YYYY"
                  )}
                </p>
              </>
            )}
            {!!consumerDisconnectionDate && (
              <>
                <p className="m-0">{t("Disconnection Date")}</p>
                <p className="fw-semibold mb-1">
                  {convertUTCDateToLocalDate(consumerDisconnectionDate).format(
                    "DD-MM-YYYY"
                  )}
                </p>
              </>
            )}
          </Grid>
          {!!(
            consumerNumber ||
            consumerName ||
            demandEstimateReceiptUrl ||
            demandEstimateUrl
          ) && (
            <Grid item lg={6} md={6}>
              {!!consumerNumber && (
                <>
                  <p className="m-0">{t("Consumer Number")}</p>
                  <p className="fw-semibold mb-1">{consumerNumber}</p>
                </>
              )}
              {!!consumerName && (
                <>
                  <p className="m-0">{t("Consumer Name")}</p>
                  <p className="fw-semibold mb-1">{consumerName}</p>
                </>
              )}
              {isOtherUtility && !!salesOrder && (
                <>
                  <p className="m-0">{t("Sales Order")}</p>
                  <p className="fw-semibold mb-1">{salesOrder}</p>
                </>
              )}
              {isSolidWasteApplication && !!serialNumber && (
                <>
                  <p className="m-0">{t("Serial Number")}</p>
                  <p className="fw-semibold mb-1">{serialNumber}</p>
                </>
              )}
              {isViewDemandEstimateBtnVisible && (
                <>
                  <p className="m-0">{t("Demand Estimate")}</p>
                  <Button
                    onClick={() =>
                      setDocumentToPreview({
                        url: getImageUrl(demandEstimateUrl),
                        documentTypeName: "Demand Estimate",
                        documentType: "pdf",
                        documentName: `${
                          applicationTypeName ?? ""
                        }-demand-estimate`,
                      })
                    }
                    variant="outlined"
                    size="small"
                  >
                    {t("View")}
                  </Button>
                </>
              )}

              {!!demandEstimateReceiptUrl && (
                <>
                  <p className="m-0">{t("Payment Receipt")}</p>
                  {demandEstimateReceiptUrl.split(",").map((url, index) => (
                    <Button
                      className="me-1"
                      key={index}
                      onClick={() =>
                        setDocumentToPreview({
                          url: getImageUrl(url),
                          documentTypeName: t("Payment Receipt"),
                          documentType: "pdf",
                          documentName: `${
                            applicationTypeName ?? ""
                          }-demand-estimate-receipt`,
                        })
                      }
                      variant="outlined"
                      size="small"
                    >
                      {t("View")}
                    </Button>
                  ))}
                </>
              )}
            </Grid>
          )}
        </Grid>
      </Grid>
    </>
  );
};

ApplicationInfoView.propTypes = {
  applicationInfo: PropTypes.object,
  isExpandable: PropTypes.bool,
  isOtherUtility: PropTypes.bool,
  t: PropTypes.func,
};

export default withTranslation()(ApplicationInfoView);
